import PropTypes from 'prop-types';
import React from 'react';

import {
    Carousel,
    CarouselHeadingLarge,
    CarouselItems,
    CarouselNavigation,
    CarouselViewAllButton
} from '$partials/base/carousel.jsx';

import {
    CarouselItemDispatcher,
    getCssClassForItemType
} from '$partials/carousels/index.js';

import { formatLinkBlockToUrl } from '$utilities/format.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {React.FC<PropTypes.InferProps<typeof propTypes>>}
 */
const ModuleProgrammeCarouselBlock = ({
    heading = '',
    isAccordionOrSection = false,
    isFirstInAccordion = false,
    pages = null,
    viewAllLink = null
}) => {
    // Config
    const carouselConfig = {
        slidesPerView: 'auto',
        spaceBetween: null
    };

    const items = pages.map((item, idx) => {
        if (item?.page?.__typename) {
            item.page.__typename = 'ProgrammeCarouselItemBlock';
            item.page.carouselItemIndex = idx;
            item.page.slideClassName = getCssClassForItemType('ProgrammeCarouselItemBlock', idx);

            return (
                <CarouselItemDispatcher
                    { ...item.page }
                    key={idx}
                    mobileFullWidth={true} />
            );
        }
    }).filter(Boolean);

    // Render
    return (
        <section className='relative'>
            <div className='layout-container'>
                <div className='layout-grid-negative-gutter flex'>
                    <div className='md:w-1/12' />
                    <div className='w-12/12 md:w-11/12 layout-grid-col-padding'>
                        <div className={`${isAccordionOrSection && !isFirstInAccordion ? 'border-t border-t-charcoal' : ''}`} />
                    </div>
                </div>
            </div>

            <section className={`layout-container relative overflow-hidden mt-40 mb-60${isAccordionOrSection ? ' md:my-60' : ' md:mt-60 md:mb-100'}`}>
                <Carousel swiperConfig={carouselConfig}>
                    <div
                        aria-label={heading ?? null}
                        aria-roledescription='carousel'
                        role='region'>
                        <div className='layout-grid-negative-gutter'>
                            <div className='flex flex-col md:flex-row flex-wrap md:items-center gap-y-20 md:gap-y-0 mb-60 sm:mb-35'>
                                <div className='w-1/12' />

                                <div className='w-12/12 sm:w-8/12 md:w-8/12 layout-grid-col-padding'>
                                    {heading && (
                                        <CarouselHeadingLarge>
                                            {heading}
                                        </CarouselHeadingLarge>
                                    )}
                                </div>

                                <div className='flex gap-20 layout-grid-col-padding md:ml-auto'>
                                    {formatLinkBlockToUrl(viewAllLink) && (
                                        <CarouselViewAllButton url={formatLinkBlockToUrl(viewAllLink)} />
                                    )}

                                    {items.length > 1 && (
                                        <div className='hidden md:block'>
                                            <CarouselNavigation />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className='layout-grid-negative-gutter'>
                            {/* desktop */}
                            <div className='hidden md:block'>
                                <CarouselItems items={items} />
                            </div>

                            {/* mobile */}
                            <section
                                aria-label='Programme feed'
                                className='md:hidden flex flex-col gap-y-60'
                                role='feed'>
                                {items.map((itm, idx) => {
                                    return (
                                        <article
                                            aria-label={itm.title}
                                            aria-posinset={idx}
                                            aria-setsize={items.length}
                                            key={idx}>
                                            {itm}
                                        </article>
                                    )
                                })}
                            </section>
                        </div>
                    </div>
                </Carousel>
            </section>
        </section>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    className: PropTypes.string,
    heading: PropTypes.string,
    isAccordionOrSection: PropTypes.bool,
    isFirstInAccordion: PropTypes.bool,
    pages: PropTypes.array.isRequired,
    viewAllLink: PropTypes.object
};

ModuleProgrammeCarouselBlock.propTypes = propTypes;

export default ModuleProgrammeCarouselBlock;
